import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Building2, Banknote, Activity, ArrowRight, CheckCircle2,
  TrendingUp, TrendingDown, Briefcase, Wallet, FileText, FileSpreadsheet,
  Flag, History, FlagOff, Zap, AlertTriangle, FolderOpen, UserPlus, X, Loader2,
  DollarSign, Send, Mail, Users, Layers, Link2, Check, QrCode as QrCodeIcon, Download, Clock, Shield,
} from "lucide-react";
import AddClientModal from "@/components/AddClientModal";
import BulkAddClientsModal from "@/components/BulkAddClientsModal";
import FirmTemplatesModal from "@/components/FirmTemplatesModal";
import FirmSlotDashboard from "@/components/FirmSlotDashboard";
import MagicLinksDrawer from "@/components/MagicLinksDrawer";

// "Recently connected" window — a tenant whose latest Teller sync happened
// within this many ms shows a "Fresh" pulse-dot on the workspace switcher
// and on its Clients-portal card. 24 hours by default.
const RECENT_SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;
const isRecentlySynced = (iso) => {
  if (!iso) return false;
  const ms = Date.parse(iso);
  return !Number.isNaN(ms) && Date.now() - ms <= RECENT_SYNC_WINDOW_MS;
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [historyFor, setHistoryFor] = useState(null); // {tenant_id, name}
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [billing, setBilling] = useState(null); // {total_workspaces, firm_paid_workspaces, estimated_mrr, workspaces[]}
  const [resendingTid, setResendingTid] = useState(null);
  const navigate = useNavigate();
  const { setSession, tenant: currentTenant, user: currentUser } = useAuth();
  const canAddClient = ["pro", "business"].includes(currentTenant?.plan) && currentUser?.role === "owner";

  const loadBilling = () => {
    if (!canAddClient) return;
    api.get("/firm/billing-summary")
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null));
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/memberships").then((r) => setItems(r.data)),
      api.get("/memberships/firm-rollup").then((r) => setRollup(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    loadBilling();
  };

  // Per-tenant invite_pending lookup — keyed on tenant_id so client cards
  // can show "Resend invite" without extra round-trips.
  const invitePendingByTid = (billing?.workspaces || []).reduce((acc, w) => {
    acc[w.tenant_id] = w.invite_pending;
    return acc;
  }, {});

  const resendInvite = async (tenantId, name) => {
    if (resendingTid) return;
    setResendingTid(tenantId);
    try {
      await api.post(`/firm/clients/${tenantId}/resend-invite`);
      toast.success(`Invite re-sent to ${name}`);
      loadBilling();
    } catch (e) {
      // Fallback to a canned message when the proxy returns HTML (e.g. a
      // Cloudflare 502 bubbling up) or the detail is suspiciously long.
      let msg = e?.response?.data?.detail;
      if (typeof msg !== "string" || msg.length > 180 || /<html|<!doctype/i.test(msg)) {
        msg = "Could not resend invite — email service is temporarily unavailable.";
      }
      toast.error(msg);
    } finally {
      setResendingTid(null);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openHistory = async (m) => {
    setHistoryFor({ tenant_id: m.tenant_id, name: m.tenant.name });
    setHistoryEvents([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/memberships/${m.tenant_id}/flag-history`);
      setHistoryEvents(data.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openWorkspace = async (m) => {
    if (m.is_active_for_user) {
      navigate("/app/dashboard");
      return;
    }
    setSwitching(m.tenant_id);
    try {
      const { data } = await api.post("/memberships/switch", { tenant_id: m.tenant_id });
      setSession(data.token, data.user, data.tenant);
      toast.success(`Switched to ${data.tenant.name}`);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch workspace");
    } finally {
      setSwitching(null);
    }
  };

  // Jump straight into a client's statement folder — same tenant-switch
  // flow, but lands on the Statements page (already grouped Year/Month)
  // so firm admins can audit uploads without clicking through the dashboard.
  const openStatementsFolder = async (m) => {
    if (m.is_active_for_user) {
      navigate("/app/statements");
      return;
    }
    setSwitching(m.tenant_id);
    try {
      const { data } = await api.post("/memberships/switch", { tenant_id: m.tenant_id });
      setSession(data.token, data.user, data.tenant);
      toast.success(`Opened ${data.tenant.name}'s folder`);
      navigate("/app/statements");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open folder");
    } finally {
      setSwitching(null);
    }
  };

  const toggleFlag = async (m) => {
    const next = !m.flagged;
    let reason = null;
    if (next) {
      reason = window.prompt(`Flag "${m.tenant.name}" for review.\n\nOptional: add a quick note (e.g. "missing March statement", "tax filing due").`);
      if (reason === null) return; // user hit cancel
    }
    // optimistic update
    setItems((prev) => prev.map((x) =>
      x.tenant_id === m.tenant_id
        ? { ...x, flagged: next, flag_reason: next ? (reason || null) : null }
        : x,
    ));
    try {
      await api.post(`/memberships/${m.tenant_id}/flag`, { flagged: next, reason });
      toast.success(next ? "Pinned to review queue" : "Flag removed");
    } catch (err) {
      // rollback
      setItems((prev) => prev.map((x) =>
        x.tenant_id === m.tenant_id ? { ...x, flagged: !next, flag_reason: m.flag_reason } : x,
      ));
      toast.error(err?.response?.data?.detail || "Could not update flag");
    }
  };

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Enterprise portal"
        testid="clients-header"
        actions={canAddClient ? (
          <div className="flex items-center gap-2 flex-wrap">
            <ShareInviteButton tenant={currentTenant} />
            <button
              type="button"
              onClick={() => setLinksOpen(true)}
              data-testid="magic-links-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
              title="View minted magic sign-in links"
            >
              <Shield size={14} /> <span className="hidden sm:inline">Links</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplatesOpen(true)}
              data-testid="firm-templates-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Layers size={14} /> <span className="hidden sm:inline">Templates</span>
            </button>
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-add-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Users size={14} /> <span className="hidden sm:inline">Bulk add</span><span className="sm:hidden">Bulk</span>
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              data-testid="add-client-btn"
              className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] flex items-center gap-1.5 transition-colors"
            >
              <UserPlus size={14} /> <span className="hidden sm:inline">Add client</span><span className="sm:hidden">Add</span>
            </button>
          </div>
        ) : null}
      />
      <PageBody>
        <p className="text-sm text-[var(--ll-ink-soft)] max-w-prose mb-6">
          Every workspace you have access to — your own books and your clients&apos; books — in one
          place. Open a workspace to switch into it; your JWT is re-minted so all data stays
          tenant-isolated.
        </p>

        {canAddClient && <FirmSlotDashboard />}

        {/* Firm rollup hero — aggregate KPIs across every client */}
        {rollup && items.length > 1 && (
          <div className="mb-6" data-testid="firm-rollup">
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Firm-level rollup</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      await downloadFile("/memberships/firm-rollup/export?format=pdf", "firm_rollup.pdf");
                    } catch (e) {
                      toast.error("Export failed");
                    }
                  }}
                  data-testid="export-firm-pdf"
                  className="text-xs px-3 py-1.5 bg-black text-white hover:bg-[#1A1A1A] transition-colors flex items-center gap-1.5 border"
                  style={{ borderColor: "#000000" }}
                >
                  <FileText size={12} /> Export PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      await downloadFile("/memberships/firm-rollup/export?format=xlsx", "firm_rollup.xlsx");
                    } catch (e) {
                      toast.error("Export failed");
                    }
                  }}
                  data-testid="export-firm-xlsx"
                  className="text-xs px-3 py-1.5 bg-black text-white hover:bg-[#1A1A1A] transition-colors flex items-center gap-1.5 border"
                  style={{ borderColor: "#000000" }}
                >
                  <FileSpreadsheet size={12} /> Export Excel
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "var(--ll-border)" }}>
              <RollupCell
                label="Clients"
                value={rollup.clients}
                sub={`${rollup.bank_accounts} bank account${rollup.bank_accounts === 1 ? "" : "s"}`}
                icon={Briefcase}
                tone="#EFF6FF"
              />
              <RollupCell
                label="Total Revenue"
                value={fmtMoney(rollup.total_revenue)}
                sub="Across all clients · YTD"
                icon={TrendingUp}
                tone="#ECFDF5"
              />
              <RollupCell
                label="Total Expenses"
                value={fmtMoney(rollup.total_expenses)}
                sub="Across all clients · YTD"
                icon={TrendingDown}
                tone="#FEF2F2"
              />
              <RollupCell
                label="Net Profit"
                value={fmtMoney(rollup.net_profit)}
                sub={`Cash on hand · ${fmtMoney(rollup.cash_position)}`}
                icon={Wallet}
                tone={rollup.net_profit >= 0 ? "#FEFCE8" : "#FEF2F2"}
                accent
              />
            </div>
          </div>
        )}

        {loading && <div className="ll-progress mb-6" />}

        {/* Firm billing rollup — only shown when at least one firm_pays client exists */}
        {!loading && billing && (billing.firm_paid_workspaces > 0 || billing.trial_workspaces > 0) && (
          <div
            className="ll-card-flat p-4 sm:p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
            data-testid="firm-billing-strip"
            style={{ background: "#F0F9FF", borderColor: "#BAE6FD" }}
          >
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 grid place-items-center" style={{ background: "#0b5d9e", color: "white" }}>
                <DollarSign size={18} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Firm billing</div>
                <div className="font-display font-bold text-lg leading-tight" data-testid="firm-billing-mrr">
                  {billing.firm_paid_workspaces > 0 ? (
                    <>You pay for {billing.firm_paid_workspaces} workspace{billing.firm_paid_workspaces === 1 ? "" : "s"} · ${billing.estimated_mrr.toLocaleString()}<span className="text-sm font-normal text-[var(--ll-ink-soft)]">/mo</span></>
                  ) : (
                    <>No paid workspaces yet</>
                  )}
                </div>
              </div>
            </div>
            {billing.trial_workspaces > 0 && (
              <div className="text-xs text-[var(--ll-ink-soft)] sm:border-l sm:pl-5 sm:ml-1" style={{ borderColor: "#BAE6FD" }} data-testid="firm-billing-trial">
                <div className="font-medium text-[var(--ll-ink)]">{billing.trial_workspaces} trial workspace{billing.trial_workspaces === 1 ? "" : "s"}</div>
                <div>Client picks their own plan when they claim it</div>
              </div>
            )}
            <div className="sm:ml-auto flex items-center gap-2 text-xs text-[var(--ll-ink-soft)]">
              <span className="font-mono-fin">{billing.total_workspaces}</span> total client{billing.total_workspaces === 1 ? "" : "s"}
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="ll-card-flat p-12 text-center" data-testid="clients-empty">
            <Building2 size={32} className="mx-auto mb-3 opacity-40" />
            <div className="font-display font-bold text-xl mb-1">No client workspaces yet</div>
            <div className="text-sm text-[var(--ll-ink-soft)]">
              When a client invites you to their Financials Ready workspace, it&apos;ll appear here.
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((m) => {
              const color = m.tenant.brand_color || "#1B4332";
              const isActive = m.is_active_for_user;
              return (
                <div
                  key={m.id}
                  data-testid={`client-card-${m.tenant_id}`}
                  className="ll-card p-5 flex flex-col relative"
                  style={{
                    borderColor: m.flagged ? "var(--ll-ochre)" : (isActive ? color : "var(--ll-border)"),
                    borderWidth: m.flagged ? 2 : 1,
                  }}
                >
                  {m.flagged && (
                    <div
                      className="absolute -top-2 -left-2 px-2 py-0.5 text-[9px] uppercase tracking-widest font-mono-fin font-medium flex items-center gap-1 z-10"
                      style={{ background: "var(--ll-ochre)", color: "white" }}
                      data-testid={`flag-badge-${m.tenant_id}`}
                    >
                      <Flag size={9} fill="white" strokeWidth={0} /> Flagged
                    </div>
                  )}
                  <div className="flex items-start gap-3 mb-3">
                    {m.tenant.logo_base64 ? (
                      <img
                        src={m.tenant.logo_base64.startsWith("data:")
                          ? m.tenant.logo_base64
                          : `data:image/png;base64,${m.tenant.logo_base64}`}
                        alt={m.tenant.name}
                        className="w-10 h-10 object-cover border"
                        style={{ borderColor: "var(--ll-border)" }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 grid place-items-center text-white shrink-0"
                        style={{ background: color }}
                      >
                        <Building2 size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-lg leading-tight truncate flex items-center gap-1.5" data-testid={`client-name-${m.tenant_id}`}>
                        <span className="truncate">{m.tenant.name}</span>
                        {(m.stats?.sync_errors || 0) > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 shrink-0"
                            style={{ background: "var(--ll-ochre-bg)", color: "var(--ll-ochre-ink)" }}
                            data-testid={`sync-error-badge-${m.tenant_id}`}
                            title={`${m.stats.sync_errors} bank connection${m.stats.sync_errors === 1 ? "" : "s"} in error state`}
                          >
                            <AlertTriangle size={9} fill="var(--ll-ochre)" strokeWidth={0} /> Sync error
                          </span>
                        ) : isRecentlySynced(m.stats?.last_connection_sync_at) ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 shrink-0"
                            style={{ background: "#D1FAE5", color: "#065F46" }}
                            data-testid={`fresh-badge-${m.tenant_id}`}
                            title="Bank synced in the last 24 hours"
                          >
                            <Zap size={9} fill="#059669" strokeWidth={0} /> Fresh
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mt-0.5">
                        Role · {m.role}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openHistory(m)}
                      data-testid={`flag-history-${m.tenant_id}`}
                      aria-label="View flag history"
                      title="View flag history"
                      className="w-7 h-7 grid place-items-center border transition-colors shrink-0 hover:bg-[var(--ll-mint)]"
                      style={{
                        borderColor: "var(--ll-border)",
                        background: "transparent",
                        color: "var(--ll-ink-muted)",
                      }}
                    >
                      <History size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleFlag(m)}
                      data-testid={`flag-toggle-${m.tenant_id}`}
                      aria-label={m.flagged ? "Remove flag" : "Flag for review"}
                      title={m.flagged ? (m.flag_reason ? `Flagged: ${m.flag_reason}` : "Remove flag") : "Flag for review"}
                      className="w-7 h-7 grid place-items-center border transition-colors shrink-0 hover:bg-[var(--ll-ochre-bg)]"
                      style={{
                        borderColor: m.flagged ? "var(--ll-ochre)" : "var(--ll-border)",
                        background: m.flagged ? "var(--ll-ochre-bg)" : "transparent",
                        color: m.flagged ? "var(--ll-ochre)" : "var(--ll-ink-muted)",
                      }}
                    >
                      <Flag size={12} fill={m.flagged ? "var(--ll-ochre)" : "none"} />
                    </button>
                    {isActive && (
                      <div className="text-[10px] uppercase tracking-widest px-2 py-0.5 inline-flex items-center gap-1 bg-[var(--ll-mint)] text-[#065F46]">
                        <CheckCircle2 size={10} /> Active
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 my-3">
                    <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] flex items-center gap-1">
                        <Banknote size={10} /> Accounts
                      </div>
                      <div className="font-mono-fin text-lg mt-0.5">{m.stats.bank_accounts}</div>
                    </div>
                    <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] flex items-center gap-1">
                        <Activity size={10} /> Txns · 30d
                      </div>
                      <div className="font-mono-fin text-lg mt-0.5">{m.stats.transactions_30d}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-4">
                    <div>Plan · <span className="text-[var(--ll-ink)]">{m.tenant.plan || "trial"}</span></div>
                    <div>{m.stats.last_transaction_date ? `Last txn ${fmtDate(m.stats.last_transaction_date)}` : "No activity yet"}</div>
                  </div>

                  {/* Invite-pending banner — appears when the client hasn't claimed
                      their workspace yet. One-click resend fires the magic-link email. */}
                  {invitePendingByTid[m.tenant_id] && (
                    <div
                      className="mb-3 p-2.5 border flex items-center gap-2 text-xs"
                      style={{ background: "#FEF3C7", borderColor: "#F59E0B", color: "#78350F" }}
                      data-testid={`invite-pending-${m.tenant_id}`}
                    >
                      <Mail size={12} className="shrink-0" />
                      <span className="flex-1">Invite not yet accepted</span>
                      <button
                        type="button"
                        onClick={() => resendInvite(m.tenant_id, m.tenant.name)}
                        disabled={resendingTid === m.tenant_id}
                        data-testid={`resend-invite-${m.tenant_id}`}
                        className="text-xs px-2 py-1 bg-white hover:bg-[#FDE68A] border border-[#F59E0B] text-[#78350F] flex items-center gap-1 disabled:opacity-60"
                      >
                        {resendingTid === m.tenant_id ? (
                          <><Loader2 size={10} className="animate-spin" /> Sending…</>
                        ) : (
                          <><Send size={10} /> Resend</>
                        )}
                      </button>
                    </div>
                  )}

                  <ClientSparkline trend={m.stats.trend_12m} tenantId={m.tenant_id} />

                  <div className="mt-auto flex items-center gap-2">
                    <ClientInviteLinkButton tenant={m.tenant} tenantId={m.tenant_id} />
                    <button
                      onClick={() => openStatementsFolder(m)}
                      disabled={switching === m.tenant_id}
                      data-testid={`folder-client-${m.tenant_id}`}
                      title={`${m.stats.statement_count ?? 0} statement${(m.stats.statement_count ?? 0) === 1 ? "" : "s"} on file`}
                      className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <FolderOpen size={14} />
                      <span className="hidden lg:inline">Statements</span>
                      {m.stats.statement_count != null && (
                        <span className="text-[10px] font-mono-fin px-1.5 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }}>
                          {m.stats.statement_count}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => openWorkspace(m)}
                      disabled={switching === m.tenant_id}
                      data-testid={`open-client-${m.tenant_id}`}
                      className="flex-1 text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                    >
                      {switching === m.tenant_id ? "Switching…" : isActive ? "Open dashboard" : "Switch & open"} <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PageBody>

      <AddClientModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => { setAddOpen(false); loadAll(); }}
      />
      <BulkAddClientsModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCreated={() => { setBulkOpen(false); loadAll(); }}
      />
      <FirmTemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
      />
      <MagicLinksDrawer
        open={linksOpen}
        onClose={() => setLinksOpen(false)}
      />

      <Dialog open={!!historyFor} onOpenChange={(open) => !open && setHistoryFor(null)}>
        <DialogContent className="max-w-lg" data-testid="flag-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History size={16} /> Flag history
            </DialogTitle>
            <DialogDescription className="text-[var(--ll-ink-soft)]">
              Shared timeline of every flag and un-flag action on{" "}
              <span className="font-medium text-[var(--ll-ink)]">{historyFor?.name}</span>{" "}
              by anyone in the firm.
            </DialogDescription>
          </DialogHeader>

          {historyLoading && <div className="ll-progress my-2" />}
          {!historyLoading && historyEvents.length === 0 && (
            <div className="py-8 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="flag-history-empty">
              <Flag size={22} className="mx-auto mb-2 opacity-40" />
              No flag activity yet for this workspace.
            </div>
          )}
          {!historyLoading && historyEvents.length > 0 && (
            <ol className="relative border-l border-[var(--ll-border)] ml-2 mt-2 space-y-4 pl-4 max-h-[420px] overflow-y-auto" data-testid="flag-history-list">
              {historyEvents.map((ev) => {
                const isFlag = ev.action === "flagged";
                const dotBg = isFlag ? "var(--ll-ochre)" : "var(--ll-ink-muted)";
                const Icon = isFlag ? Flag : FlagOff;
                return (
                  <li key={ev.id} className="relative" data-testid={`flag-event-${ev.id}`}>
                    <span
                      className="absolute -left-[22px] top-0.5 w-3.5 h-3.5 grid place-items-center text-white"
                      style={{ background: dotBg }}
                    >
                      <Icon size={8} fill={isFlag ? "white" : "none"} strokeWidth={2.5} />
                    </span>
                    <div className="text-xs text-[var(--ll-ink-soft)] font-mono-fin">
                      {fmtDate(ev.created_at?.slice(0, 10))} · {(ev.created_at || "").slice(11, 16)} UTC
                    </div>
                    <div className="text-sm mt-0.5">
                      <span className="font-medium">{ev.actor_name || ev.actor_email}</span>{" "}
                      <span className="text-[var(--ll-ink-soft)]">
                        {isFlag ? "pinned this workspace for review" : "removed the flag"}
                      </span>
                    </div>
                    {ev.reason && (
                      <div
                        className="mt-1.5 px-3 py-2 text-xs border-l-2 italic"
                        style={{ borderColor: "var(--ll-ochre)", background: "var(--ll-ochre-bg)", color: "var(--ll-ochre-ink)" }}
                      >
                        “{ev.reason}”
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RollupCell({ label, value, sub, icon: Icon, tone, accent = false }) {
  return (
    <div
      className="p-5"
      style={{
        background: tone || "var(--ll-bg-card)",
        borderTop: accent ? "3px solid var(--ll-ink)" : "none",
      }}
      data-testid={`rollup-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{label}</div>
        <Icon size={14} className="opacity-70" />
      </div>
      <div className="font-mono-fin text-2xl leading-none">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-2">{sub}</div>}
    </div>
  );
}

function ClientSparkline({ trend, tenantId }) {
  if (!trend || trend.length === 0) return null;
  const total = trend.reduce((acc, p) => acc + p.income + p.expense, 0);
  if (total === 0) {
    return (
      <div
        className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] text-center py-3 mb-3 border border-dashed"
        style={{ borderColor: "var(--ll-border)" }}
      >
        No activity in the last 12 months
      </div>
    );
  }
  // Last point change vs 6-month avg to render a trend chip
  const recent = trend.slice(-3).reduce((a, p) => a + p.income - p.expense, 0) / 3;
  const earlier = trend.slice(0, 6).reduce((a, p) => a + p.income - p.expense, 0) / 6;
  const delta = earlier === 0 ? 0 : ((recent - earlier) / Math.abs(earlier)) * 100;
  return (
    <div className="mb-3" data-testid={`sparkline-${tenantId}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">
        <span>12-mo Revenue · Expense</span>
        <span
          className="font-mono-fin"
          style={{ color: delta >= 0 ? "#065F46" : "#991B1B" }}
        >
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(0)}%
        </span>
      </div>
      <div className="h-12">
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <Line
              type="monotone" dataKey="income" stroke="#10B981" strokeWidth={1.5}
              dot={false} isAnimationActive={false}
            />
            <Line
              type="monotone" dataKey="expense" stroke="#F87171" strokeWidth={1.5}
              dot={false} isAnimationActive={false}
            />
            <Tooltip
              formatter={(v, k) => [fmtMoney(v), k === "income" ? "Income" : "Expense"]}
              labelFormatter={(l) => l}
              contentStyle={{
                background: "#fff", border: "1px solid #1B4332",
                borderRadius: 0, fontSize: 11, fontFamily: "IBM Plex Mono", padding: "4px 8px",
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}



/**
 * One-click "Share branded invite link" + QR code popover.
 * Copies either /signup?f=<slug> (preferred, after an admin sets a slug in
 * Settings) or /signup?tenant=<id> (UUID fallback — long but always works)
 * so prospective clients land in the firm's palette + logo immediately.
 * The QR button opens a popover with a printable SVG QR for business
 * cards / lobby displays.
 */
function ShareInviteButton({ tenant }) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const param = tenant?.slug ? `f=${tenant.slug}` : (tenant?.id ? `tenant=${tenant.id}` : "");
  const link = param ? `${origin}/signup?${param}` : "";
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(tenant?.slug
        ? "Branded signup link copied"
        : "Signup link copied (set a slug in Settings for a shorter URL)");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };
  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={copy}
        disabled={!link}
        data-testid="share-invite-link-btn"
        title={link || ""}
        className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5 disabled:opacity-60"
        style={{ borderColor: "var(--ll-border)" }}
      >
        {copied ? <Check size={14} /> : <Link2 size={14} />}
        <span className="hidden sm:inline">{copied ? "Copied" : "Share invite"}</span>
      </button>
      <button
        type="button"
        onClick={() => setQrOpen((v) => !v)}
        disabled={!link}
        data-testid="share-invite-qr-btn"
        title="Show QR code"
        aria-label="Show QR code for invite link"
        className="text-sm px-2 py-2 border hover:bg-[var(--ll-mint)] disabled:opacity-60"
        style={{ borderColor: "var(--ll-border)" }}
      >
        <QrCodeIcon size={14} />
      </button>
      {qrOpen && link && (
        <InviteQrPopover link={link} name={tenant?.name} onClose={() => setQrOpen(false)} />
      )}
    </div>
  );
}

/**
 * Per-client magic-link generator rendered on each workspace card. Pops a
 * TTL picker (7/30/90 days), POSTs to /api/firm/clients/<id>/magic-link to
 * mint a signed JWT, then copies `/magic?t=<jwt>` to the clipboard. Links
 * are self-expiring server-side so a stale lobby QR can't let a terminated
 * client back in — the TTL is enforced on the next exchange attempt.
 */
function ClientInviteLinkButton({ tenant, tenantId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Click-outside / Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const mintAndCopy = async (ttl_days) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/firm/clients/${tenantId}/magic-link`, { ttl_days });
      const link = `${origin}/magic?t=${data.token}`;
      await navigator.clipboard.writeText(link);
      setCopied(true);
      const expiresOn = new Date(data.expires_at).toLocaleDateString();
      toast.success(`Magic link for ${data.target_tenant_name} copied · expires ${expiresOn}`);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mint link");
    } finally { setBusy(false); }
  };

  return (
    <div ref={popRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={`client-copy-link-${tenantId}`}
        aria-label={`Copy magic sign-in link for ${tenant?.name || "client"}`}
        title="Mint & copy sign-in link"
        className="w-9 h-9 grid place-items-center border hover:bg-[var(--ll-mint)] shrink-0 disabled:opacity-60"
        style={{ borderColor: "var(--ll-border)" }}
      >
        {copied ? <Check size={14} /> : (busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />)}
      </button>
      {open && (
        <div
          className="absolute left-0 bottom-full mb-2 z-30 bg-white border p-3 shadow-md w-[200px]"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid={`client-link-ttl-popover-${tenantId}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
            <Clock size={11} /> Link expires in
          </div>
          <div className="space-y-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => mintAndCopy(d)}
                disabled={busy}
                data-testid={`client-link-ttl-${d}-${tenantId}`}
                className="w-full text-left text-xs px-2 py-1.5 border hover:bg-[var(--ll-row-hover)] disabled:opacity-60"
                style={{ borderColor: "var(--ll-border)" }}
              >
                {d} day{d === 1 ? "" : "s"}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[var(--ll-ink-soft)] mt-2 leading-snug">
            One-click sign-in. Expires server-side.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Lightweight popover that renders the invite link as a scannable QR.
 * Uses qrcode.react — SVG output is sharp at any zoom and safe to print
 * directly from the browser. Click outside / Esc / Close button to dismiss.
 */
function InviteQrPopover({ link, name, onClose }) {
  const qrRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Serialize the live SVG to a Blob — crisp vector output for printers.
  const downloadSvg = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    triggerDownload(blob, "svg");
  };

  // Rasterize the SVG to a 512x512 PNG for marketing collateral that
  // prefers bitmaps (Canva, Google Docs, Photoshop).
  const downloadPng = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      canvas.toBlob((blob) => {
        if (blob) triggerDownload(blob, "png");
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast.error("Could not render PNG"); };
    img.src = url;
  };

  const triggerDownload = (blob, ext) => {
    const safeName = (name || "signup").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "signup";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}-signup-qr.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${ext.toUpperCase()}`);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        data-testid="qr-popover-backdrop"
      />
      <div
        className="absolute right-0 top-full mt-2 z-50 bg-white border p-4 shadow-md w-[240px]"
        style={{ borderColor: "var(--ll-border)" }}
        data-testid="qr-popover"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            Signup QR
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="qr-popover-close"
            className="w-5 h-5 grid place-items-center hover:bg-[var(--ll-mint)]"
            aria-label="Close QR popover"
          >
            <X size={11} />
          </button>
        </div>
        <div ref={qrRef} className="grid place-items-center p-2 border mb-2" style={{ borderColor: "var(--ll-border)" }}>
          <QRCodeSVG value={link} size={168} level="M" includeMargin={false} data-testid="qr-code-svg" />
        </div>
        <div className="text-[10px] text-[var(--ll-ink-soft)] text-center truncate mb-2" title={link}>
          {name || "Share this QR"}
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={downloadPng}
            data-testid="qr-download-png"
            className="flex-1 text-xs px-2 py-1.5 border hover:bg-[var(--ll-row-hover)] inline-flex items-center justify-center gap-1"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <Download size={11} /> PNG
          </button>
          <button
            type="button"
            onClick={downloadSvg}
            data-testid="qr-download-svg"
            className="flex-1 text-xs px-2 py-1.5 border hover:bg-[var(--ll-row-hover)] inline-flex items-center justify-center gap-1"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <Download size={11} /> SVG
          </button>
        </div>
      </div>
    </>
  );
}

